import React, { useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { BellRingIcon } from "lucide-react";
import { notificationSchema } from "../../schemas/notificationFormSchema";
import Multiselect from "../../components/ui/Multiselect";
import { useUsers } from "../../hooks/useUsers";
import { useQuery } from "@tanstack/react-query";

export default function SubscriptionForm({
  mode = "view",
  defaultValues = {
    header: "",
    body: "",
    notification_type: "",
    user_id: [],
  },
  onSubmit,
  onCancel,
}) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(notificationSchema),
    defaultValues: { ...defaultValues, mode },
  });
  const [users, setUsers] = React.useState([]);
  const { fetchUsers } = useUsers({
    filters: {
      status: "active",
      filter: JSON.stringify({ field: "user_type", value: "user" }),
    },
    sort: { field: "createdAt", direction: "desc" },
    pagination: { page: 1, limit: 0 },
  });

  const userQuery = useQuery({
    queryKey: ["users"],
    queryFn: fetchUsers,
    keepPreviousData: true,
    enabled: watch("notification_type") === "MESSAGE",
  });

  useEffect(() => {
    if (userQuery.data && !userQuery.isLoading) {
      const formattedUsers = userQuery?.data?.data?.users || [];
      setUsers(formattedUsers);
    }
  }, [userQuery.data, userQuery.isLoading]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-1">
            Title
            <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              {...register("header")}
              readOnly={mode === "view"}
              className="px-3 py-2 border rounded w-full"
              placeholder="E.g., Broadcast Notification"
            />
          </div>
          {errors.header && (
            <p className="text-red-500 text-xs">{errors.header.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Message</label>
          <textarea
            {...register("body")}
            readOnly={mode === "view"}
            className="px-3 py-2 border rounded w-full"
            placeholder="Describe the notification plan"
            rows={2}
          />
          {errors.body && (
            <p className="text-red-500 text-xs">{errors.body.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Notification Type
            <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <BellRingIcon className="h-5 w-5 text-gray-400" />
            </div>
            <select
              {...register("notification_type")}
              disabled={mode === "view"}
              className="pl-10 pr-3 py-2 border rounded w-full"
            >
              <option value="">Select Notification Type</option>
              <option value="BROADCAST">Broadcast</option>
              <option value="MESSAGE">Message To User</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
          {errors.notification_type && (
            <p className="text-red-500 text-xs">
              {errors.notification_type.message}
            </p>
          )}
        </div>

        {watch("notification_type") === "MESSAGE" && (
          <div>
            <label className="block text-sm font-medium mb-1">
              {mode === "view" ? "Selected" : "Select"} User
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Multiselect
                options={users}
                selected={watch("user_id") || []}
                onChange={(selected) => {
                  setValue("user_id", selected);
                }}
                placeholder="Select users"
                disabled={mode === "view"}
                labelKey="full_name"
                valueKey="_id"
                className="py-2 w-full"
              />
            </div>
            {errors.user_id && (
              <p className="text-red-500 text-xs">{errors.user_id.message}</p>
            )}
          </div>
        )}
      </div>
      {/* Action buttons */}
      <div className="px-6 py-4 border-t flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          {mode === "view" ? "Close" : "Cancel"}
        </button>
        {mode !== "view" && (
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 rounded-md text-white bg-primary hover:bg-primary-700"
          >
            {mode === "edit" ? "Save Changes" : "Send"}
          </button>
        )}
      </div>
    </form>
  );
}
